import { get } from "lodash";
import { toastr } from "react-redux-toastr";
import { AxiosError, AxiosResponse } from "axios";

// Custom
import { Group } from "../../utils/logger";

// Get API Configs
import InternalApi from "../../utils/axios.InternalConfig";

// Types
import { Dispatch } from "redux";

import {
  USER_LOADING,
  LOAD_USER,
  REFRESH_USER,
  SSO_USER,
  SSO_COMPLETE,
  LOGOUT_USER,
  AUTH_ERROR,
  GLOBAL_BLOCK_UI,
  GLOBAL_RELEASE_UI,
  AUTH_TOKEN,
  RESET_USER,
  REGISTER_USER,
  LIST_USER,
  LIST_USER_ERROR,
  UPDATE_USER,
  AUTH_ERROR_TITLE,
  AUTH_ERROR_MSG,
} from "../constants";
import { UserPayload } from "../../types/user";

const LoggerGroup = new Group("User Actions", { disable: false });

// ================================================================================================
// User Operations ================================================================================
// ================================================================================================

type TokenParams = {
  token: string;
  account_number: string;
  user_number?: string;
};

function handleAuthError(process: string, err: AxiosError) {
  const error = get(err, "response.data.error") || err.message || "Unknown Error";

  // If specific issue with provided credentials, display error text
  // Else show generic message in toast and specific in console
  if (err.response && [400, 401, 403, 404].includes(err.response.status)) {
    err.response && toastr.error("Sorry", err.response.data.error);
  } else if (err.response && [429].includes(err.response.status)) {
    toastr.error("Too Many Failed Attempts", err.response.data.error);
  } else {
    toastr.error("Sorry", "There was an unexpected error signing you in");
  }

  LoggerGroup.emit({ warn: `${process} Error` }) // Custom error log
    .emit({ error }) // Log the Axios Error
    .end(); // Send to console immediately

  throw err;
}

function handleAuthSuccess(process: string, dispatch: Dispatch, res: AxiosResponse) {
  const payload = {
    details: res.data.user, // User Object { id, name_vch, permissions: ["switchboard.read"] }
    userKey: res.data.client_key, // Client Name "dlc"
    userToken: res.data.token, // JWT for subsequent calls
  };

  // Pat: reversed order to fix auth header not being set when SSO_USER is called
  dispatch({ type: AUTH_TOKEN, payload: res.data.token });
  dispatch({ type: SSO_USER, payload });
  LoggerGroup.emit(`${process} RESPONSE`, { table: res.data }).end();
}

function handleResetSuccess(process: string, dispatch: Dispatch, res: AxiosResponse) {
  switch (res.data.status) {
    case 2:
      toastr.success(
        "Success",
        "You have successfully reset your password, please sign in to verify setup is complete."
      );
      break;
    case -1:
      toastr.error(
        "Expired",
        `Token has expired, please resubmit email address to reset password.`
      );
      break;
    case -2:
      toastr.error("Invalid", `Token is invalid, please resubmit email address to reset password.`);
      break;
    default:
      toastr.error("Sorry", "There was an unexpected error resetting your password.");
  }

  dispatch({ type: RESET_USER, payload: res.data.status });

  LoggerGroup.emit(`${process} RESPONSE`, { table: res.data }).end();
}

function handleResetError(process: string, err: AxiosError) {
  const error = get(err, "response.data.error") || err.message || "Unknown Error";

  // If specific issue with provided credentials, display error text
  // Else show generic message in toast and specific in console
  if (err.response && [400, 401, 404].includes(err.response.status)) {
    err.response && toastr.error("Sorry", err.response.data.error);
  } else {
    toastr.error("Sorry", "There was an unexpected error resetting your password");
  }

  LoggerGroup.emit({ warn: `${process} Error` }) // Custom error log
    .emit({ error }) // Log the Axios Error
    .end(); // Send to console immediately

  throw err;
}

function handleForgotSuccess(process: string, dispatch: Dispatch, res: AxiosResponse) {
  toastr.success(
    "Success",
    "An email has been sent to the provided address with instructions to reset password."
  );

  dispatch({ type: RESET_USER, payload: 1 });

  LoggerGroup.emit(`${process} RESPONSE`, { table: res.data }).end();
}

function handleForgotError(process: string, err: AxiosError) {
  const error = get(err, "response.data.error") || err.message || "Unknown Error";

  // If specific issue with provided credentials, display error text
  // Else show generic message in toast and specific in console
  if (err.response && [400, 401, 404].includes(err.response.status)) {
    err.response && toastr.error("Sorry", err.response.data.error);
  } else {
    toastr.error(
      "Sorry",
      "There was an unexpected error processing your request to reset your password."
    );
  }

  LoggerGroup.emit({ warn: `${process} Error` }) // Custom error log
    .emit({ error }) // Log the Axios Error
    .end(); // Send to console immediately

  throw err;
}

export const loginWithToken = (token: string, params: TokenParams) => (dispatch: Dispatch) => {
  LoggerGroup.emit("userActions.logInWithToken starting");

  dispatch({ type: USER_LOADING });
  dispatch({ type: GLOBAL_BLOCK_UI });

  const headers: any = {
    Authorization: `Bearer ${token}`,
    "x-account-number": params.account_number, // Required Header
  };

  if (params.user_number) {
    LoggerGroup.emit(`  [+] appending user_number[${params.user_number}]`);
    headers["x-user-number"] = params.user_number; // Optional header
  }

  return InternalApi.get("auth/client-portal-token", { headers })
    .then(handleSuccess)
    .catch(handleAuthError.bind(null, "userActions.logInWithToken"))
    .finally(() => dispatch({ type: GLOBAL_RELEASE_UI }));

  function handleSuccess(res: AxiosResponse) {
    // Don't store Auth Token until account has been verified
    dispatch({ type: AUTH_TOKEN, payload: res.data.token });
    dispatch({ type: LOAD_USER, payload: res.data.details });

    // put a registerAccount in here, have registerAccount call getAccount
    // dispatch(registerAccount(res.data.details.account) as any);

    LoggerGroup.emit("Auth Token Response").emit({ table: res.data }).end();
  }
};

export const loginWithPassword = (user: any) => (dispatch: Dispatch) => {
  LoggerGroup.emit("userActions.ssoUser starting");

  // Password should already be encoded
  // Encode password
  // user.password = btoa(user.password);

  dispatch({ type: USER_LOADING });
  dispatch({ type: GLOBAL_BLOCK_UI });

  InternalApi.post("auth/login", user)
    .then(handleAuthSuccess.bind(null, "userActions.loginWithPassword", dispatch))
    .catch(handleAuthError.bind(null, "userActions.loginWithPassword"))
    .finally(() => dispatch({ type: GLOBAL_RELEASE_UI }));
};

type Vendor = "azure";
export const ssoUser =
  (vendor: Vendor, authData: any, emailDomain: string = "") =>
  (dispatch: Dispatch) => {
    LoggerGroup.emit("userActions.ssoUser starting");

    dispatch({ type: USER_LOADING });
    dispatch({ type: GLOBAL_BLOCK_UI });

    return InternalApi.post("auth/sso", { vendor, authData, emailDomain })
      .then(handleAuthSuccess.bind(null, "userActions.ssoUser", dispatch))
      .catch(handleAuthError.bind(null, "userActions.ssoUser"))
      .finally(() => dispatch({ type: GLOBAL_RELEASE_UI }));
  };

export const ssoComplete = (vendor: Vendor, authData: any) => (dispatch: Dispatch) => {
  dispatch({
    type: SSO_COMPLETE,
    payload: {
      vendor,
      authData,
    },
  });
};

/**
 * Get user record and apply to the state
 */
export const loadUser = () => (dispatch: any) => {
  LoggerGroup.emit("userActions.loadUser starting");

  dispatch({ type: USER_LOADING });

  // Need to retrieve user data
  LoggerGroup.emit("Loading user from API Call");

  InternalApi.get("user")
    .then(handleSuccess) // Handle Success Response
    .catch(handleError); // Handle Error Response

  function handleSuccess(res: AxiosResponse) {
    dispatch({ type: LOAD_USER, payload: res.data });
    LoggerGroup.emit("LOAD USER RESPONSE", { table: res.data }).end();
  }

  function handleError(err: AxiosError) {
    const errors = get(err, "response.data.errors") || err.message;
    dispatch({ type: AUTH_ERROR, payload: errors });

    LoggerGroup.emit({ warn: "userActions.loadUser Error" }) // Custom error log
      .emit({ error: errors }) // Log the Axios Error
      .end(); // Send to console immediately

    // Log user out - invalid state
    dispatch({ type: LOGOUT_USER });
  }
};

export const refreshUser = () => (dispatch: Dispatch, getState: any) => {
  const { user: currentUser } = getState();

  InternalApi.get(`user/manage/${currentUser.details.id}`)
    .then(handleSuccess) // handle success response
    .catch(handleError); // handle error response

  function handleSuccess(res: AxiosResponse) {
    dispatch({ type: REFRESH_USER, payload: res.data });
    LoggerGroup.emit("REFRESH USER RESPONSE", { table: res.data }).end();
  }

  function handleError(err: AxiosError) {
    const errors = get(err, "response.data.errors") || err.message;
    dispatch({ type: AUTH_ERROR, payload: errors });

    LoggerGroup.emit({ warn: "userActions.refreshUser Error" }) // Custom error log
      .emit({ error: errors }) // Log the Axios Error
      .end(); // Send to console immediately

    // Log user out - invalid state
    dispatch({ type: LOGOUT_USER });
  }
};

/**
 * Logs a user out by removing user information from the state
 */
export const logoutUser = () => (dispatch: Dispatch) => {
  return new Promise((resolve) => {
    toastr.confirm("Are you sure you want to log out?", {
      okText: "Sign Out",
      cancelText: "Cancel",
      onCancel: () => LoggerGroup.emit("Cancelled logout").end(),
      onOk: async () => {
        dispatch({ type: LOGOUT_USER });
        dispatch({ type: "RESET_APP" });
        resolve(null);

        toastr.success("Success", "You have been logged out");
      },
    } as any);
  });
};

export const resetPassword =
  (password: string, userId: string, tenant: string, token: string) => (dispatch: Dispatch) => {
    LoggerGroup.emit("userActions.resetPassword starting");

    // Password should already be encoded
    // Encode password
    const encodedPassword = btoa(password);

    InternalApi.post("auth/reset", {
      password: encodedPassword,
      userId,
      tenant,
      token,
    })
      .then(handleResetSuccess.bind(null, "userActions.resetPassword", dispatch))
      .catch(handleResetError.bind(null, "userActions.resetPassword"))
      .finally(() => dispatch({ type: GLOBAL_RELEASE_UI }));
  };

export const forgotPassword = (email: string, tenant?: string) => (dispatch: Dispatch) => {
  LoggerGroup.emit("userActions.forgotPassword starting");

  InternalApi.post("auth/forgot", {
    email,
    tenant,
  })
    .then(handleForgotSuccess.bind(null, "userActions.forgotPassword", dispatch))
    .catch(handleForgotError.bind(null, "userActions.forgotPassword"))
    .finally(() => dispatch({ type: GLOBAL_RELEASE_UI }));
};

export const registerUser =
  (name: string, email: string, permissions: string[], cppPermissions: number[]) =>
  (dispatch: Dispatch) => {
    LoggerGroup.emit("userActions.registerUser starting");

    InternalApi.post("user/register", {
      name,
      email,
      permissions,
      cppPermissions,
    })
      .then(handleRegisterSuccess.bind(null, "userActions.registerUser", dispatch))
      .catch(handleRegisterError.bind(null, "userActions.registerUser"))
      .finally(() => dispatch({ type: GLOBAL_RELEASE_UI }));
  };

function handleRegisterSuccess(process: string, dispatch: Dispatch, res: AxiosResponse) {
  // Success Register
  toastr.success("Success", `User has been registered for [${res.data.email}].`);
  dispatch({ type: REGISTER_USER, payload: true });
  LoggerGroup.emit(`${process} RESPONSE`, { table: res.data }).end();

  // Send Forgot Password Email
  dispatch(forgotPassword(res.data.email, res.data.tenant) as any);
}

function handleRegisterError(process: string, err: AxiosError) {
  const error = get(err, "response.data.error") || err.message || "Unknown Error";

  // If specific issue with provided credentials, display error text
  // Else show generic message in toast and specific in console
  if (err.response && [400, 401, 403, 404].includes(err.response.status)) {
    err.response && toastr.error("Sorry", err.response.data.error);
  } else if (err.response && [409].includes(err.response.status)) {
    toastr.error("Sorry", `Email address has already been registered in CSR Portal.`);
  } else {
    toastr.error("Sorry", "There was an unexpected error registering user.");
  }

  LoggerGroup.emit({ warn: `${process} Error` }) // Custom error log
    .emit({ error }) // Log the Axios Error
    .end(); // Send to console immediately

  throw err;
}

/**
 * Get user list and apply to the state
 */
export const getUserList =
  (pageLimit: number, pageNumber: number, search?: string) => (dispatch: any) => {
    // Need to retrieve user data
    LoggerGroup.emit("Loading user list from API Call");

    const queryString = search ? `?search=${encodeURIComponent(search)}` : "";

    InternalApi.get(`user/list${queryString}`, {
      headers: {
        "x-page-limit": pageLimit,
        "x-page-number": pageNumber,
      },
    })
      .then(handleSuccess) // Handle Success Response
      .catch(handleError); // Handle Error Response

    function handleSuccess(res: AxiosResponse) {
      dispatch({ type: LIST_USER, payload: res.data });
      LoggerGroup.emit("LOADED USER LIST RESPONSE", { table: res.data }).end();
    }

    function handleError(err: AxiosError) {
      const error = get(err, "response.data.errors") || err.message;
      dispatch({ type: LIST_USER_ERROR, payload: error });

      LoggerGroup.emit({ warn: "Get User List Error" }) // Custom error log
        .emit({ error }) // Log the Axios Error
        .end(); // Send to console immediately

      if (error.includes(401)) {
        toastr.error(AUTH_ERROR_TITLE, AUTH_ERROR_MSG);
      } else {
        toastr.error(error, err.message);
      }
    }
  };

export const updateUser =
  (userPayload: UserPayload, onFinished?: any) => (dispatch: Dispatch, getState: any) => {
    LoggerGroup.emit("userActions.updateUser starting");

    const { user: currentUser } = getState();
    const { userId, name, email, permissions, cppPermissions, active, password } = userPayload;

    InternalApi.put(`user/manage/${userId}`, {
      name,
      email,
      permissions,
      cppPermissions,
      active,
      password,
    })
      .then(handleSuccess)
      .catch(handleError);

    function handleSuccess(res: AxiosResponse) {
      toastr.success("Success", `User information has been updated for [${email}].`);
      dispatch({ type: UPDATE_USER, payload: res.data });
      LoggerGroup.emit("userActions.updateUser success", { table: res.data }).end();

      if (onFinished) {
        onFinished();
      }

      console.log("STATE COMPARISON", userPayload, currentUser);
      if (userId === currentUser.details.id) {
        LoggerGroup.emit("Updated current user! Refresh user settings...");
        dispatch(refreshUser() as any);
      }

      LoggerGroup.end();
    }

    function handleError(err: AxiosError) {
      const error = get(err, "response.data.errors") || err.message;
      dispatch({ type: LIST_USER_ERROR, payload: error });

      toastr.error(
        "Sorry",
        `An unexpected error occurred while updating user information for [${email}].`
      );

      LoggerGroup.emit({ warn: "userActions.updateUser error" }) // Custom error log
        .emit({ error }) // Log the Axios Error
        .end(); // Send to console immediately

      if (error.includes(401)) {
        toastr.error(AUTH_ERROR_TITLE, AUTH_ERROR_MSG);
      } else {
        toastr.error(error, err.message);
      }
    }
  };

/*
export const getUser = (userId: number) => (dispatch: Dispatch) => {
  LoggerGroup.emit("userActions.getUser starting");

  InternalApi.get(`user/manage/${userId}`)
    .then(handleSuccess)
    .catch(handleError)
  
  function handleSuccess(res: AxiosResponse) {
    toastr.success("Success", `User information retrieved for userId[${userId}].`);
    dispatch({ type: GET_USER, payload: res.data });
    LoggerGroup.emit("userActions.getUser success", { table: res.data }).end();
  }

  function handleError(err: AxiosError) {
    const error = get(err, "response.data.errors") || err.message;
    dispatch({ type: LIST_USER_ERROR, payload: error });

    toastr.error("Sorry", `An unexpected error occurred while getting user information for userId[${userId}].`);

    LoggerGroup.emit({ warn: "userActions.getUser error" }) // Custom error log
      .emit({ error }) // Log the Axios Error
      .end(); // Send to console immediately

    if (error.includes(401)) {
      toastr.error(AUTH_ERROR_TITLE, AUTH_ERROR_MSG);
    } else {
      toastr.error(error, err.message);
    }
  }
}
*/

export const resetStatus = () => (dispatch: any) => {
  dispatch({ type: RESET_USER, payload: 0 });
};
